/**
 * RATE MANAGEMENT - Update vendor pricing without SQL
 * Allows global admins to manage rate cards via UI
 */

import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, Calendar, CheckCircle } from 'lucide-react';
import FinancialNav from '../components/admin/financials/FinancialNav';
import fetchWithCsrf from '../lib/fetchWithCsrf';

// Cost types billed per million tokens (use perMillionInput helper)
const PER_MILLION_COST_TYPES = new Set([
  // OpenAI
  'gpt4o_input_token', 'gpt4o_output_token',
  'gpt4o_mini_input_token', 'gpt4o_mini_output_token',
  'gpt35_input_token', 'gpt35_output_token',
  'text_embedding_3_small_token',
  // Anthropic
  'claude_input_token', 'claude_output_token',
  'claude_opus_input_token', 'claude_opus_output_token',
  'claude_haiku_input_token', 'claude_haiku_output_token',
]);

export default function RateManagement() {
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [perMillionInput, setPerMillionInput] = useState('');
  const [formData, setFormData] = useState({
    provider: 'twilio',
    cost_type: '',
    unit_cost: '',
    effective_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const isPerMillionType = PER_MILLION_COST_TYPES.has(formData.cost_type);

  const handlePerMillionChange = (val) => {
    setPerMillionInput(val);
    const perToken = val ? (parseFloat(val) / 1_000_000).toFixed(10) : '';
    setFormData(f => ({ ...f, unit_cost: perToken }));
  };

  // Known cost types per provider — must match what costTracking uses
  const COST_TYPES = {
    anthropic: [
      { value: 'claude_input_token',        label: 'Claude Sonnet — Input tokens' },
      { value: 'claude_output_token',       label: 'Claude Sonnet — Output tokens' },
      { value: 'claude_opus_input_token',   label: 'Claude Opus — Input tokens' },
      { value: 'claude_opus_output_token',  label: 'Claude Opus — Output tokens' },
      { value: 'claude_haiku_input_token',  label: 'Claude Haiku — Input tokens' },
      { value: 'claude_haiku_output_token', label: 'Claude Haiku — Output tokens' },
    ],
    openai: [
      { value: 'gpt4o_input_token',        label: 'GPT-4o — Input tokens' },
      { value: 'gpt4o_output_token',        label: 'GPT-4o — Output tokens' },
      { value: 'gpt4o_mini_input_token',    label: 'GPT-4o Mini — Input tokens' },
      { value: 'gpt4o_mini_output_token',   label: 'GPT-4o Mini — Output tokens' },
      { value: 'gpt35_input_token',         label: 'GPT-3.5 Turbo — Input tokens' },
      { value: 'gpt35_output_token',        label: 'GPT-3.5 Turbo — Output tokens' },
      { value: 'text_embedding_3_small_token', label: 'text-embedding-3-small — Per token' },
    ],
    twilio: [
      { value: 'sms_segment_outbound',  label: 'SMS Segment — Outbound' },
      { value: 'sms_segment_inbound',   label: 'SMS Segment — Inbound' },
      { value: 'mms_message_outbound',  label: 'MMS Message — Outbound' },
    ],
    supabase: [],
    other: [],
  };

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    setLoading(true);
    try {
      const response = await fetchWithCsrf('/api/admin/financials/rate-cards');
      if (response.ok) {
        const data = await response.json();
        setRates(data || []);
      }
    } catch (err) {
      console.error('Error loading rates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetchWithCsrf('/api/admin/financials/rate-cards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          unit_cost: parseFloat(formData.unit_cost)
        })
      });

      if (response.ok) {
        alert('Rate added successfully!');
        setShowAddModal(false);
        setFormData({
          provider: 'twilio',
          cost_type: '',
          unit_cost: '',
          effective_date: new Date().toISOString().split('T')[0],
          notes: ''
        });
        loadRates();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to add rate'}`);
      }
    } catch (err) {
      console.error('Error adding rate:', err);
      alert('Error adding rate');
    }
  };

  const handleEndRate = async (rateId) => {
    if (!window.confirm('End this rate? It will stop being used for cost calculations after today.')) {
      return;
    }

    try {
      const response = await fetchWithCsrf(`/api/admin/financials/rate-cards/${rateId}/end`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          end_date: new Date().toISOString().split('T')[0]
        })
      });

      if (response.ok) {
        alert('Rate ended successfully!');
        loadRates();
      } else {
        alert('Error ending rate');
      }
    } catch (err) {
      console.error('Error ending rate:', err);
      alert('Error ending rate');
    }
  };

  // Group rates by provider
  const groupedRates = rates.reduce((acc, rate) => {
    if (!acc[rate.provider]) acc[rate.provider] = [];
    acc[rate.provider].push(rate);
    return acc;
  }, {});

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-600" />
            Rate Management
          </h1>
          <p className="text-gray-600 mt-2">Update vendor pricing without writing SQL</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-6 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add New Rate
        </button>
      </div>

      {/* Navigation */}
      <FinancialNav />

      {/* Quick Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">💡 How to update pricing:</h3>
        <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
          <li><strong>End the old rate</strong> - Click "End Rate" on the current rate (sets end_date to today)</li>
          <li><strong>Add new rate</strong> - Click "Add New Rate" and enter the new pricing (effective tomorrow or future date)</li>
          <li><strong>Done!</strong> - Cost tracking automatically uses the correct rate based on event date</li>
        </ol>
      </div>

      {/* Rates by Provider */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading rates...</p>
        </div>
      ) : (
        Object.entries(groupedRates).map(([provider, providerRates]) => (
          <div key={provider} className="bg-white rounded-lg shadow mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-900 capitalize">{provider}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost Type</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effective Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {providerRates.map((rate) => {
                    const isActive = !rate.end_date;
                    const isFuture = new Date(rate.effective_date) > new Date();
                    return (
                      <tr key={rate.id} className={isActive ? '' : 'bg-gray-50 opacity-60'}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {rate.cost_type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-mono">
                          ${parseFloat(rate.unit_cost).toFixed(8)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {new Date(rate.effective_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {rate.end_date ? new Date(rate.end_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-6 py-4">
                          {isActive ? (
                            isFuture ? (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Scheduled
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Ended
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                          {rate.notes || '—'}
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          {isActive && !isFuture && (
                            <button
                              onClick={() => handleEndRate(rate.id)}
                              className="text-red-600 hover:text-red-900 font-medium"
                            >
                              End Rate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* Add Rate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Add New Rate</h2>
              <form onSubmit={handleAddRate} className="space-y-4">
                {/* Provider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                  <select
                    value={formData.provider}
                    onChange={(e) => { setPerMillionInput(''); setFormData({ ...formData, provider: e.target.value, cost_type: '', unit_cost: '' }); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="anthropic">Anthropic</option>
                    <option value="twilio">Twilio</option>
                    <option value="openai">OpenAI</option>
                    <option value="supabase">Supabase</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Cost Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cost Type</label>
                  {COST_TYPES[formData.provider]?.length > 0 ? (
                    <select
                      value={formData.cost_type}
                      onChange={(e) => { setPerMillionInput(''); setFormData({ ...formData, cost_type: e.target.value, unit_cost: '' }); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">— Select cost type —</option>
                      {COST_TYPES[formData.provider].map(ct => (
                        <option key={ct.value} value={ct.value}>{ct.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.cost_type}
                      onChange={(e) => setFormData({ ...formData, cost_type: e.target.value })}
                      placeholder="e.g., storage_gb"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  )}
                  <p className="text-xs text-gray-500 mt-1 font-mono">{formData.cost_type || 'Select a cost type above'}</p>
                </div>

                {/* Unit Cost */}
                <div>
                  {isPerMillionType ? (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price <span className="font-normal text-gray-500">($ per 1M tokens — enter exactly as listed on the provider's pricing page)</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={perMillionInput}
                        onChange={(e) => handlePerMillionChange(e.target.value)}
                        placeholder="e.g., 2.50"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                      {perMillionInput && (
                        <p className="text-xs text-green-700 mt-1 font-mono">
                          Stored as: ${(parseFloat(perMillionInput) / 1_000_000).toFixed(10)} per token
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Unit Cost ($)</label>
                      <input
                        type="number"
                        step="0.00000001"
                        value={formData.unit_cost}
                        onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                        placeholder="0.0079"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter cost per unit in dollars</p>
                    </>
                  )}
                </div>

                {/* Effective Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Effective Date</label>
                  <input
                    type="date"
                    value={formData.effective_date}
                    onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">When this rate takes effect</p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="e.g., New pricing from vendor announcement"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
                  >
                    Add Rate
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
